import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAppStore } from "../../../core/store/store";
import { createApi } from "../../../core/api";

type LoginTab = "email" | "username";
type ForgotMode = "email" | "username";

const LOGIN_EMAIL = true;
const LOGIN_USERNAME = true;
const FORGOT_PASSWORD_ENABLED = true;
const COMPANY_NAME = "Fixer Aviation";

const getInitialTab = (): LoginTab => {
  if (LOGIN_EMAIL) return "email";
  return "username";
};

export const Login = () => {
  const api = useMemo(() => createApi(), []);
  const token = useAppStore((s) => s.auth?.token);
  const login = useAppStore((s) => s.authActions.login);
  const loc = useLocation() as any;
  const from = loc.state?.from || "/dashboard";

  const [tab, setTab] = useState<LoginTab>(getInitialTab());
  const [forgot, setForgot] = useState(false);
  const [forgotMode, setForgotMode] = useState<ForgotMode>("email");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState({
    email: "",
    username: "",
    password: "",
  });

  useEffect(() => {
    setTab(getInitialTab());
  }, []);

  if (token) return <Navigate to={from} replace />;

  const doLogin = async () => {
    debugger;
    if (loading) return;

    setLoading(true);
    setError(null);

    try {
      let res: any;

      if (tab === "username") {
        if (!form.username || !form.password)
          throw new Error("Username and password required");

        res = await api.auth.login({
          username: form.username,
          password: form.password,
        });
      }

      if (tab === "email") {
        if (!form.email || !form.password)
          throw new Error("Email and password required");

        res = await api.auth.loginEmail({
          username: form.email,
          password: form.password,
        });
      }

      if (!res || res.ok === false)
        throw new Error(res?.message || "Login failed");

      const body = res.data.data ?? res.data;

      login({
        token: body.token,
        expiresAt: body.expireAt,
        user: {
          id: body.userId,
          companyId: body.companyId,
          username: body.username,
          email: body.email,
          firstName: body.firstName,
          lastName: body.lastName,
          role: body.role,
          permissions: body.permissons ?? [],
          profilePhotoUrl: body.profilPhoto?.url ?? null,
        },
      });
    } catch (e: any) {
      setError(e?.message || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  const sendForgot = async () => {
    if (loading) return;

    setLoading(true);
    setError(null);

    try {
      if (forgotMode === "email") {
        if (!form.email) throw new Error("Email required");
        await api.user.forgetPasswordByEmail(form.email);
      }

      if (forgotMode === "username") {
        if (!form.username) throw new Error("Username required");
        await api.user.forgetPasswordByUsername(form.username);
      }
    } catch (e: any) {
      setError(e?.message || "Reset request failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ ...S.page, justifyContent: "center" }}>
      <div style={S.loginWrap}>
        <div style={S.card}>
          <div style={S.logoCircle}>
            <img src="/img/minLogo.png" style={S.imgCircle} />
          </div>

          <div style={S.brand}>{COMPANY_NAME}</div>

          {!forgot && (
            <div style={S.tabsWrap}>
              {LOGIN_EMAIL && (
                <div
                  style={{
                    ...S.tabItem,
                    background: tab === "email" ? "#ffffff22" : "transparent",
                  }}
                  onClick={() => setTab("email")}
                >
                  Email
                </div>
              )}
              {LOGIN_USERNAME && (
                <div
                  style={{
                    ...S.tabItem,
                    background:
                      tab === "username" ? "#ffffff22" : "transparent",
                  }}
                  onClick={() => setTab("username")}
                >
                  Username
                </div>
              )}
            </div>
          )}

          {!forgot && tab === "email" && (
            <>
              <input
                style={S.input}
                placeholder="Email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
              <input
                style={S.input}
                type="password"
                placeholder="Password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
              />
              <button style={S.button} onClick={doLogin} disabled={loading}>
                Login
              </button>
            </>
          )}

          {!forgot && tab === "username" && (
            <>
              <input
                style={S.input}
                placeholder="Username"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
              />
              <input
                style={S.input}
                type="password"
                placeholder="Password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
              />
              <button style={S.button} onClick={doLogin} disabled={loading}>
                Login
              </button>
            </>
          )}

          {FORGOT_PASSWORD_ENABLED && !forgot && (
            <div style={S.link} onClick={() => setForgot(true)}>
              Forgot password?
            </div>
          )}

          {forgot && (
            <>
              <div style={S.tabsWrap}>
                <div
                  style={{
                    ...S.tabItem,
                    background:
                      forgotMode === "email" ? "#ffffff22" : "transparent",
                  }}
                  onClick={() => setForgotMode("email")}
                >
                  Email
                </div>
                <div
                  style={{
                    ...S.tabItem,
                    background:
                      forgotMode === "username" ? "#ffffff22" : "transparent",
                  }}
                  onClick={() => setForgotMode("username")}
                >
                  Username
                </div>
              </div>

              {forgotMode === "email" && (
                <input
                  style={S.input}
                  placeholder="Email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              )}

              {forgotMode === "username" && (
                <input
                  style={S.input}
                  placeholder="Username"
                  value={form.username}
                  onChange={(e) =>
                    setForm({ ...form, username: e.target.value })
                  }
                />
              )}

              <button style={S.button} onClick={sendForgot} disabled={loading}>
                Send reset link
              </button>

              <div style={S.link} onClick={() => setForgot(false)}>
                Back to login
              </div>
            </>
          )}

          {error && <div style={S.errorBox}>{error}</div>}
        </div>
      </div>
    </div>
  );
};

const S: Record<string, React.CSSProperties> = {
  page: { display: "flex", minHeight: "100vh", background: "#dee2e6" },
  loginWrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  card: {
    position: "relative",
    width: 440,
    padding: "86px 36px 40px",
    background: "#374151",
    borderRadius: 28,
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },
  logoCircle: {
    position: "absolute",
    top: -46,
    left: "50%",
    transform: "translateX(-50%)",
    width: 92,
    height: 92,
    borderRadius: "50%",
    background: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  imgCircle: { width: 65, height: 65, borderRadius: "50%" },
  brand: { textAlign: "center", color: "#fff", fontSize: 22, fontWeight: 600 },
  tabsWrap: { display: "flex", gap: 10, justifyContent: "center" },
  tabItem: {
    padding: "10px 14px",
    borderRadius: 999,
    color: "#fff",
    cursor: "pointer",
  },
  input: { height: 48, borderRadius: 999, padding: "0 18px", border: "none" },
  button: {
    height: 50,
    borderRadius: 999,
    border: "none",
    background: "#E30613",
    color: "#fff",
    fontWeight: 600,
  },
  link: {
    textAlign: "center",
    color: "#d1d5db",
    fontSize: 13,
    cursor: "pointer",
  },
  errorBox: {
    marginTop: 8,
    padding: "10px 14px",
    borderRadius: 8,
    background: "#fee2e2",
    color: "#991b1b",
    fontSize: 13,
    textAlign: "center",
  },
};
